import { gql } from '@apollo/client';
import type { Asset, AssetTypeInput } from '#/graphql/types';
import { buildFilters } from '#/common/utils/buildFilters';
import {
  getAssetType,
  getAssetTypeList,
  getBuildingList,
  getLocation,
  getManufacturer,
  getManufacturerList,
  getModel,
  getModelList,
} from '#/graphql/queries';
import { inUseStatuses } from '../manufacturer/utils/constants';
import LocationItem from './locationItemRenderer';
import FieldDefs from '#/types/FieldDefs';
import { getBaseColumns } from '#/common/utils/baseColumns';
import { states } from './mappings';
import { LINK_TRANE_SUPPLY } from '#/config/consts';
import { unknownMappingValue } from '#/common/components/ColumnMapper/ColumnMapper';
import assetTypeFields from '../assetType/utils/fields';
import manufacturerFields from '../manufacturer/utils/fields';
import modelFields from '../model/utils/fields';

const currentYear = new Date().getFullYear();

const toNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

const fields = {
  ...getBaseColumns(),
  id: {
    dataField: 'id',
    label: 'ID',
    editorOptions: { readOnly: true },
  },
  displayNumber: {
    dataField: 'displayNumber',
    label: 'Number',
    isRequired: true,
    editorOptions: { readOnly: true, placeholder: 'Auto generated' },
  },
  assetTag: {
    dataField: 'assetTag',
    label: 'Asset Tag',
    isRequired: true,
    editorType: 'TextBox',
  },
  userId: {
    dataField: 'userId',
    label: 'user ID',
  },
  description: {
    dataField: 'description',
    label: 'Description',
    isRequired: true,
    editorType: 'TextArea',
    editorOptions: {
      placeholder: 'Enter description',
      autoResizeEnabled: true,
      height: 100,
    },
  },
  locationId: {
    dataField: 'locationId',
    label: 'Location',
    isRequired: true,
    editorType: 'RelatedItems',
    editorOptions: {
      placeholder: 'Search location...',
      button: {
        tooltip: 'View location details',
        url: '/assets/locations/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'name',
      valueExpr: 'id',
      dataStoreOptions: {
        getItemList: gql(getBuildingList),
        getItem: gql(getLocation),
        filter: ['inUseStatus', '=', inUseStatuses.inUse.key],
      },
      itemRender: LocationItem,
    },
  },
  makeId: {
    dataField: 'makeId',
    label: 'Manufacturer',
    isRequired: true,
    editorType: 'RelatedItems',
    editorOptions: {
      placeholder: 'Search manufacturers...',
      button: {
        tooltip: 'View manufacturer details',
        url: '/assets/manufacturers/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'name',
      valueExpr: 'id',
      dataStoreOptions: {
        getItemList: gql(getManufacturerList),
        getItem: gql(getManufacturer),
        filter: ['inUseStatus', '=', inUseStatuses.inUse.key],
      },
      subtitleExpr: 'contactPerson',
      createItemAction: {
        label: '+ New Manufacturer',
        onPrimaryAction: async ({ values, formData, onCreate, alert }) => {
          const tenantId = toNonEmptyString(formData?.tenantId);

          const name = toNonEmptyString(values?.name) ?? '';
          if (!name) {
            await alert('error', 'Name is required.');
            return { keepOpen: true };
          }

          const response = await onCreate.createManufacturer({
            name,
            inUseStatus: inUseStatuses.inUse.key,
            tenantId,
          });

          if (response?.errors?.length) {
            await alert('error', response.errors.filter(Boolean).join('; '));
            return { keepOpen: true };
          }

          const createdId = response?.data?.id;
          if (!createdId) {
            await alert('error', 'Could not create manufacturer.');
            return { keepOpen: true };
          }

          return { selectedValue: createdId };
        },
        modal: {
          title: 'Create New Manufacturer',
          fields: [manufacturerFields.name],
          infoMessage:
            'Showing required fields only. Configure full details anytime in Assets > Manufacturers.',
          cancelLabel: 'Cancel',
          primaryLabel: 'Create',
        },
      },
    },
  },
  modelId: {
    dataField: 'modelId',
    label: 'Model',
    isRequired: true,
    editorType: 'RelatedItems',
    editorOptions: {
      placeholder: 'Search models...',
      button: {
        tooltip: 'View model details',
        url: '/assets/models/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'name',
      valueExpr: 'id',
      dataStoreOptions: {
        getItemList: gql(getModelList),
        getItem: gql(getModel),
        filter: ['inUseStatus', '=', inUseStatuses.inUse.key],
      },
      subtitleExpr: 'manufacturer.name',
      createItemAction: {
        label: '+ New Model',
        getInitialValues: ({ formData }) => ({
          assetTypeId: toNonEmptyString(formData?.assetTypeId),
          manufacturerId: toNonEmptyString(formData?.makeId),
        }),
        onPrimaryAction: async ({ values, formData, onCreate, alert }) => {
          const tenantId = toNonEmptyString(formData?.tenantId);

          const name = toNonEmptyString(values?.name) ?? '';
          if (!name) {
            await alert('error', 'Name is required.');
            return { keepOpen: true };
          }

          const assetTypeId = toNonEmptyString(values?.assetTypeId);
          if (!assetTypeId) {
            await alert('error', 'Asset Type is required.');
            return { keepOpen: true };
          }

          const manufacturerId = toNonEmptyString(values?.manufacturerId);
          if (!manufacturerId) {
            await alert('error', 'Manufacturer is required.');
            return { keepOpen: true };
          }

          const response = await onCreate.createModel({
            name,
            assetTypeId,
            manufacturerId,
            inUseStatus: inUseStatuses.inUse.key,
            tenantId,
          });

          if (response?.errors?.length) {
            await alert('error', response.errors.filter(Boolean).join('; '));
            return { keepOpen: true };
          }

          const createdId = response?.data?.id;
          if (!createdId) {
            await alert('error', 'Could not create model.');
            return { keepOpen: true };
          }

          return { selectedValue: createdId };
        },
        modal: {
          title: 'Create New Model',
          fields: [
            modelFields.name,
            {
              ...modelFields.assetTypeId,
              editorOptions: {
                ...modelFields.assetTypeId.editorOptions,
                readOnly: true,
              },
            },
            {
              ...modelFields.manufacturerId,
              editorOptions: {
                ...modelFields.manufacturerId.editorOptions,
                readOnly: true,
              },
            },
          ],
          infoMessage:
            'Showing required fields only. Configure full details anytime in Assets > Models.',
          cancelLabel: 'Cancel',
          primaryLabel: 'Create',
        },
      },
      dynamic: {
        watch: ['assetTypeId', 'makeId'],
        build: ({ record }) => {
          const typeId = record?.assetTypeId as string | undefined;
          const makeId = record?.makeId as string | undefined;

          if (!typeId && !makeId) return null;

          const filterParts: string[][] = [];
          if (typeId) filterParts.push(['assetTypeId', '=', typeId]);
          if (makeId) filterParts.push(['manufacturerId', '=', makeId]);

          return buildFilters(filterParts);
        },

        // Example: react to parent changes (telemetry, side-effects, etc.)
        // onWatchChange: ({ changed, form, record, value }) => {
        //   console.log('Parents changed:', changed);
        // },
      },
    },
  },
  state: {
    dataField: 'state',
    label: 'Status',
    isRequired: true,
    editorType: 'SelectBox',
    editorOptions: {
      placeholder: 'Choose an option',
      items: [
        { key: states.pending.key, label: states.pending.label },
        { key: states.in_stock.key, label: states.in_stock.label },
        { key: states.installed.key, label: states.installed.label },
        { key: states.retired.key, label: states.retired.label },
      ],
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  usefulLife: {
    dataField: 'usefulLife',
    label: 'Useful Life (years)',
    editorType: 'NumberBox',
    editorOptions: {
      placeholder: 'Enter a number',
      min: 0,
      max: 1000,
      step: 1,
      format: '#0',
      showSpinButtons: true,
    },
  },
  acquisitionCostValue: {
    dataField: 'acquisitionCostValue',
    label: 'Acquisition Cost',
    editorType: 'Currency',
    editorOptions: {
      placeholder: 'Enter amount',
      min: 0,
      format: {
        type: 'decimal',
      },
    },
  },
  lastMaintenanceDate: {
    dataField: 'lastMaintenanceDate',
    label: 'Last Maintenance Date',
    editorType: 'DateBox',
    editorOptions: {
      type: 'date',
      placeholder: 'Select date',
      showClearButton: true,
    },
  },
  nextScheduledMaintenanceDate: {
    dataField: 'nextScheduledMaintenanceDate',
    label: 'Next Scheduled Maintenance Date',
    editorType: 'DateBox',
    editorOptions: {
      type: 'date',
      placeholder: 'Select date',
      showClearButton: true,
    },
  },
  assignedToId: {
    dataField: 'assignedToId',
    label: 'Assign To ID',
    isRequired: true,
  },
  assetTypeId: {
    dataField: 'assetTypeId',
    label: 'Asset Type',
    isRequired: true,
    editorType: 'RelatedItems',
    editorOptions: {
      placeholder: 'Search asset types...',
      button: {
        tooltip: 'View asset type details',
        url: '/assets/types/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'name',
      valueExpr: 'id',
      dataStoreOptions: {
        getItemList: gql(getAssetTypeList),
        getItem: gql(getAssetType),
        filter: ['inUseStatus', '=', inUseStatuses.inUse.key],
      },
      subtitleExpr: 'category',
      createItemAction: {
        label: '+ New Asset Type',
        onPrimaryAction: async ({ values, formData, onCreate, alert }) => {
          const tenantId = toNonEmptyString(formData?.tenantId);

          const defaultRiskScoreValue = values?.defaultRiskScore;
          let defaultRiskScore: number | undefined;
          if (typeof defaultRiskScoreValue === 'number') {
            defaultRiskScore = defaultRiskScoreValue;
          } else if (typeof defaultRiskScoreValue === 'string') {
            defaultRiskScore = Number(defaultRiskScoreValue);
          }

          const name = toNonEmptyString(values?.name);
          if (!name) {
            await alert('error', 'Name is required.');
            return { keepOpen: true };
          }

          const payload: AssetTypeInput = {
            name,
            category: typeof values?.category === 'string' ? values.category : undefined,
            defaultRiskScore,
            inUseStatus: inUseStatuses.inUse.key,
            tenantId,
          };

          const response = await onCreate.createAssetType(payload);

          if (response?.errors?.length) {
            await alert('error', response.errors.filter(Boolean).join('; '));
            return { keepOpen: true };
          }

          const createdId = response?.data?.id;
          if (!createdId) {
            await alert('error', 'Could not create asset type.');
            return { keepOpen: true };
          }

          return { selectedValue: createdId };
        },
        modal: {
          title: 'Create New Asset Type',
          fields: [
            assetTypeFields.name,
            assetTypeFields.category,
            assetTypeFields.defaultRiskScore,
          ],
          infoMessage:
            'Showing required fields only. Configure full details anytime in Assets > Types.',
          cancelLabel: 'Cancel',
          primaryLabel: 'Create',
        },
      },
    },
  },
  condition: {
    dataField: 'condition',
    label: 'Condition',
    editorType: 'SelectBox',
    editorOptions: {
      placeholder: 'Choose an option',
      items: [
        { key: 'good', label: 'Good' },
        { key: 'fair', label: 'Fair' },
        { key: 'poor', label: 'Poor' },
      ],
      displayExpr: 'label',
      valueExpr: 'key',
      showClearButton: true,
    },
  },
  preventiveMaintenanceCost: {
    dataField: 'preventiveMaintenanceCost',
    label: 'Preventive Maintenance Cost',
    editorType: 'Currency',
    editorOptions: {
      placeholder: 'Calculated automatically',
      min: 0,
      readOnly: true,
      format: {
        type: 'decimal',
      },
    },
  },
  correctiveMaintenanceCost: {
    dataField: 'correctiveMaintenanceCost',
    label: 'Corrective Maintenance Cost',
    editorType: 'Currency',
    editorOptions: {
      placeholder: 'Calculated automatically',
      min: 0,
      readOnly: true,
      format: {
        type: 'decimal',
      },
    },
  },
  maintenanceCostValue: {
    dataField: 'maintenanceCostValue',
    label: 'Maintenance Cost',
    editorType: 'Currency',
    editorOptions: {
      placeholder: 'Enter amount',
      min: 0,
      format: {
        type: 'decimal',
      },
    },
  },
  currentBookValue: {
    dataField: 'currentBookValue',
    label: 'Current Book Value',
    editorType: 'Currency',
    editorOptions: {
      placeholder: 'Enter amount',
      min: 0,
      format: {
        type: 'currency',
      },
    },
  },
  installDate: {
    dataField: 'installDate',
    label: 'Install Date',
    editorType: 'DateBox',
    editorOptions: {
      type: 'date',
      placeholder: 'Select date',
      showClearButton: true,
    },
  },
  decommissionDate: {
    dataField: 'decommissionDate',
    label: 'Decommission Date',
    editorType: 'DateBox',
    editorOptions: {
      type: 'date',
      placeholder: 'Select date',
      showClearButton: true,
    },
  },
  image: {
    dataField: 'image',
    label: 'Image',
    editorType: 'Image',
    editorOptions: {
      alt: 'Selected image',
      height: '100%',
    },
    cssClass: 'hide-label',
  },
  utilizationData: {
    dataField: 'utilizationData',
    label: 'Utilization Data',
  },
  reasonForDecommission: {
    dataField: 'reasonForDecommission',
    label: 'Reason For Decommission',
    editorType: 'TextArea',
  },
  serialNumber: {
    dataField: 'serialNumber',
    label: 'Serial Number',
    isRequired: true,
  },
  thirdPartyId: {
    dataField: 'thirdPartyId',
    label: 'Third Party ID',
  },
  totalCostOfOwnershipValue: {
    dataField: 'totalCostOfOwnershipValue',
    label: 'Total Cost Of Ownership',
    editorType: 'Currency',
    editorOptions: {
      placeholder: 'Enter amount',
      min: 0,
      format: {
        type: 'currency',
      },
    },
  },
  repairCostValue: {
    dataField: 'repairCostValue',
    label: 'RepairCost',
    editorType: 'Currency',
    editorOptions: {
      placeholder: 'Enter amount',
      min: 0,
      format: {
        type: 'currency',
      },
    },
  },
  replacementCost: {
    dataField: 'replacementCost',
    label: 'Replacement Cost',
    editorType: 'Currency',
    editorOptions: {
      placeholder: 'Enter amount',
      min: 0,
      format: {
        type: 'decimal',
      },
    },
  },
  replacementYear: {
    dataField: 'replacementYear',
    label: 'Replacement Year',
    editorType: 'DateBox',
    editorOptions: {
      type: 'date',
      placeholder: 'Select year',
      pickerType: 'calendar',
      displayFormat: 'yyyy',
      openOnFieldClick: true,
      useMaskBehavior: true,
      calendarOptions: {
        minZoomLevel: 'decade',
        maxZoomLevel: 'decade',
      },
      min: new Date(currentYear, 0, 1),
      max: new Date(currentYear + 20, 11, 31),
      showClearButton: true,
    },
  },
  warrantyExpiration: {
    dataField: 'warrantyExpiration',
    label: 'Warranty Expiration',
    editorType: 'DateBox',
    editorOptions: {
      type: 'date',
      placeholder: 'Select date',
      showClearButton: true,
    },
  },
  downTime: {
    dataField: 'downTime',
    label: 'Down Time',
    editorType: 'DateBox',
    editorOptions: {
      type: 'date',
      placeholder: 'Select date',
      showClearButton: true,
    },
  },
  purchaseOrderId: {
    dataField: 'purchaseOrderId',
    label: 'Purchase Order ID',
  },
  model: {
    dataField: 'model.name',
    label: 'Model',
  },
  manufacturer: {
    dataField: 'manufacturer.name',
    label: 'Manufacturer',
  },
  assetType: {
    dataField: 'assetType.name',
    label: 'Asset Type',
  },
  woCount: {
    dataField: 'woCount',
    label: 'Work Order Count',
  },
  findingsCount: {
    dataField: 'findingsCount',
    label: 'Findings Count',
  },
  tenant: {
    dataField: 'tenant.name',
    label: 'Organization',
    isRequired: true,
    editorOptions: { readOnly: true },
  },
  location: {
    dataField: 'location.name',
    label: 'Location',
    isRequired: true,
    editorOptions: { readOnly: true },
  },
  assetRisk: {
    dataField: 'model.riskScore',
    label: 'Asset Risk',
    editorOptions: { readOnly: true },
  },
  riskAssessmentScore: {
    dataField: 'riskAssessmentScore',
    label: 'Risk Assessment Score',
    editorType: 'NumberBox',
    editorOptions: {
      placeholder: 'Enter a number',
      min: 0,
      max: 5,
      step: 0.01,
      showSpinButtons: true,
    },
  },
  riskScoreDriverNote: {
    dataField: 'riskScoreDriverNote',
    label: 'Risk Score Driver Note',
    editorType: 'TextArea',
    editorOptions: {
      placeholder: 'Enter note',
      autoResizeEnabled: true,
    },
  },
  traneSupplySearch: {
    editorType: 'SplitButton',
    dataField: 'traneSupplySearch',
    label: false, // label is provided on the button itself
    editorOptions: {
      mainBtnTitle: 'Search Parts',
      buttons: [
        {
          title: 'By Model',
          baseUrl: `${LINK_TRANE_SUPPLY}:value`,
          addFieldValueToUrl: 'model.name',
          disabled: (record: Asset) =>
            !record.model?.name || record.model.name.trim() === unknownMappingValue.label,
        },
        {
          title: 'By Serial Number',
          baseUrl: `${LINK_TRANE_SUPPLY}:value`,
          addFieldValueToUrl: 'serialNumber',
          disabled: (record: Asset) =>
            !record.serialNumber || record.serialNumber.trim() === unknownMappingValue.label,
        },
      ],
    },
  },
} satisfies FieldDefs<Asset & { traneSupplySearch: unknown }>;

export default fields;
